import { get } from "svelte/store";
import { userStore } from "../stores/user-store.js";
import type { UserStore } from "../stores/user-store.js";
import { getEnvironment } from "../../config/env.js";
import type { Project, ProjectData, ProjectsData, ProjectInput } from "./types.js";
import type { Customer, CustomerData, CustomersData, CustomerInput } from "./types.js";
import type { User, UserData, UsersData, UserMetadataInput, UserInput } from "./types.js";
import type { ProjectUpload, ProjectUploadsData } from "./types.js";
import type { ViewsData, View, Result, ResultsData } from "./types.js";
import type { Document, DocumentsData } from "./types.js";
import { uploadStore } from "../stores/upload-store.js";
import { api } from "./api-client.js";
import { logInfo, logWarning, logError } from "../logging/log-handler.js";

/******************************************** */
/* GENERAL */
/******************************************** */

class TokenExpiredError extends Error {
  constructor() {
    super("Token has expired");
    this.name = "TokenExpiredError";
  }
}

function getUserStore(): UserStore & { user: User } {
  const store = get(userStore);
  if (!store.user) {
    throw new TokenExpiredError();
  }

  // Check token exists
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new TokenExpiredError();
  }

  // Check token expiration from localStorage
  const expiration = localStorage.getItem("tokenExpiration");
  const currentTimeUTCSeconds = Math.floor(Date.now() / 1000); // Convert milliseconds to seconds for UTC comparison

  if (!expiration || parseInt(expiration) < currentTimeUTCSeconds) {
    const readableExpiration = localStorage.getItem("tokenExpirationReadable");
    logWarning("Token expired", "data-handler", {}, { readableExpiration });
    userStore.signOut();
    throw new TokenExpiredError();
  }

  return store as UserStore & { user: User };
}

export async function getElasticSearchKey(): Promise<string> {
  try {
    const store = getUserStore();
    const response = await api.get("/getElasticSearchKey", {
      object_id: store.user.oid,
    });
    return response.data.elasticSearchKey;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getElasticSearchKey" });
    throw error;
  }
}

export async function getSASUrl(customer_code: string, project_id: string, filename: string): Promise<string> {
  try {
    const store = getUserStore();

    const response = await api.get("/getSASUrl", { customer_code, project_id, filename, object_id: store.user.oid });
    return response.data.sasUrl;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getSASUrl" });
    throw error;
  }
}

/******************************************** */
/* CUSTOMER */
/******************************************** */

export async function getCustomers(): Promise<CustomersData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getCustomers", { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getCustomers" });
    return { customers: [] };
  }
}

export async function getMyCustomers(): Promise<CustomersData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getCustomersByUser", { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getMyCustomers" });
    return { customers: [] };
  }
}

export async function getCustomer(customer_id: string): Promise<CustomerData> {
  try {
    const store = getUserStore();
    const url = `/getCustomer`;
    const response = await api.get(url, { customer_id, object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getCustomer", customer_id });
    return { customer: {} as Customer, lookups: [] };
  }
}

export async function addCustomer(input: CustomerInput): Promise<CustomerData> {
  try {
    const store = getUserStore();
    const response = await api.post("/addCustomer", input, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler",undefined, { operation: "addCustomer", customerName: input.name, customerCode: input.code });
    return { customer: {} as Customer, lookups: [] };
  }
}

export async function updateCustomer(input: CustomerInput): Promise<CustomerData> {
  try {
    const store = getUserStore();
    const response = await api.put("/updateCustomer", input, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler",undefined, { operation: "updateCustomer", customer_id: input.customer_id, customerName: input.name, customerCode: input.code }); 
    return { customer: {} as Customer, lookups: [] };
  }
}

export async function deleteCustomer(customer_id: string): Promise<boolean> {
  try {
    const store = getUserStore();
    const response = await api.delete("/deleteCustomer", { customer_id, object_id: store.user.oid });
    return response.success;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "deleteCustomer", customer_id });
    return false;
  }
}

export async function generateCustomer(): Promise<CustomerData> {
  try {
    const store = getUserStore();
    const response = await api.get("/generateCustomer", { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "generateCustomer" });
    return { customer: {} as Customer, lookups: [] };
  }
}

/******************************************** */
/* PROJECT */
/******************************************** */

export async function getProjects(): Promise<ProjectsData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getProjects", { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getProjects" });
    return { projects: [] };
  }
}

export async function getMyProjects(): Promise<ProjectsData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getProjectsByUser", { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getMyProjects" });
    return { projects: [] };
  }
}

export async function getProjectsByCustomer(customer_id: string): Promise<ProjectsData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getProjectsByCustomer", { customer_id, object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
      logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getProjectsByCustomer", customer_id });
    return { projects: [] };
  }
}

export async function getProject(project_id: string): Promise<ProjectData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getProject", { project_id, object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getProject", project_id });
    return { project: {} as Project, lookups: [] };
  }
}

export async function addProject(input: ProjectInput): Promise<ProjectData> {
  try {
    const store = getUserStore();
    const response = await api.post("/addProject", input, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "addProject", projectName: input.name, customer_id: input.customer_id });
    return { project: {} as Project, lookups: [] };
  }
}

export async function updateProject(input: ProjectInput): Promise<ProjectData> {
  try {
    const store = getUserStore();
    const response = await api.put("/updateProject", input, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "updateProject", project_id: input.project_id, projectName: input.name, customer_id: input.customer_id });
    return { project: {} as Project, lookups: [] };
  }
}

export async function deleteProject(project_id: string): Promise<boolean> {
  try {
    const store = getUserStore();
    const response = await api.delete("/deleteProject", { project_id, object_id: store.user.oid });
    return response.success;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "deleteProject", project_id });
    return false;
  }
}

export async function generateProject(customer_id: string): Promise<ProjectData> {
  try {
    const store = getUserStore();
    const response = await api.get("/generateProject", { customer_id, object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "generateProject", customer_id });
    return { project: {} as Project, lookups: [] };
  }
}

/******************************************** */
/* PROJECT UPLOAD */
/******************************************** */

export async function initializeProjectUpload(project_id: string): Promise<number> {
  const store = getUserStore();
  const response = await api.post(`/initializeProjectUpload?project_id=${project_id}&object_id=${store.user.oid}`);
  return response.version;
}

const CHUNK_SIZE = 1024 * 1024 * 5; // 5MB chunks

export async function uploadProjectFiles(
  project_id: string,
  file: File,
  version: number,
): Promise<{
  version: number;
  chunkIndex: number;
  totalChunks: number;
  chunkSize: number;
}> {
  const store = getUserStore();
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // Initialize the upload store with starting state
  uploadStore.updateProgress({
    version,
    chunkIndex: 0,
    totalChunks,
    chunkSize: CHUNK_SIZE,
  });

  try {
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const chunkFile = new File([chunk], "chunk.zip", {
        type: "application/zip",
      });

      const formData = new FormData();
      formData.append("file", chunkFile);
      formData.append("version", version.toString());
      formData.append("chunkIndex", chunkIndex.toString());
      formData.append("totalChunks", totalChunks.toString());

      const data = await api.post("/uploadProjectFiles", formData, { project_id, object_id: store.user.oid });

      if (!data.success) {
        uploadStore.setFailed();
        throw new Error(`Upload failed at chunk ${chunkIndex}`);
      }

      // Update store with current chunk progress after successful upload
      uploadStore.updateProgress({
        version,
        chunkIndex: chunkIndex + 1,
        totalChunks,
        chunkSize: chunk.size,
      });

      // Only return on the very last chunk
      if (chunkIndex === totalChunks - 1) {
        return {
          version,
          chunkIndex: totalChunks, // Set to total chunks for completion
          totalChunks,
          chunkSize: chunk.size,
        };
      }
    }
  } catch (error) {
    uploadStore.setFailed();
    throw error;
  }

  throw new Error("Unexpected end of upload");
}

export async function getProjectUploads(project_id: string): Promise<ProjectUploadsData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getProjectUploads", { project_id, object_id: store.user.oid });
    return response.data;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getProjectUploads", project_id });
    return { project_uploads: [] };
  }
}

/******************************************** */
/* DOCUMENTS */
/******************************************** */

export async function getDocumentsByCustomer(customer_id: string): Promise<DocumentsData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getDocumentsByCustomer", { customer_id, object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getDocumentsByCustomer", customer_id });
    return { documents: [] };
  }
}

/******************************************** */
/* USER */
/******************************************** */

export async function getUsers(): Promise<UsersData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getUsers", { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getUsers" });
    return { users: [] };
  }
}

export async function getUser(user_id: string): Promise<UserData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getUser", { object_id: store.user.oid, user_id });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getUser", user_id });
    return { user: {} as User };
  }
}

export async function addUser(input: UserInput): Promise<UserData> {
  try {
    const store = getUserStore();
    const response = await api.post("/addUser", input, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "addUser", user_id: input.user_id, userEmail: input.email });
    throw error;
  }
}

export async function updateUser(input: UserInput): Promise<UserData> {
  try {
    const store = getUserStore();
    const response = await api.put("/updateUser", input, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "updateUser", user_id: input.user_id, userEmail: input.email });
    return { user: {} as User };
  }
}

export async function deleteUser(user_id: string): Promise<boolean> {
  try {
    const store = getUserStore();
    const response = await api.delete("/deleteUser", { user_id, object_id: store.user.oid });
    return response.success;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "deleteUser", user_id });
    return false;
  }
}

export async function generateUser(): Promise<UserData> {
  try {
    const store = getUserStore();
    const data = await api.get("/generateUser", { object_id: store.user.oid });
    return data.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "generateUser" });
    return { user: {} as User };
  }
}

/* Only used to get user from database, after successful auth */
export async function getUserByOID(object_id: string): Promise<UserData> {
  try {
    const response = await api.get("/getUserByOID", { object_id });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getUserByOID", object_id });
    return { user: {} as User };
  }
}

export async function updateUserMetadata(input: UserMetadataInput): Promise<UserData> {
  try {
    const store = getUserStore();
    const response = await api.put("/updateUserMetadata", input, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "updateUserMetadata" });
    return { user: {} as User };
  }
}

export async function saveUserMetadata(metadata: UserMetadataInput): Promise<void> {
  const store = getUserStore();
  try {
    const response = await api.put("/updateUserMetadata", metadata, { object_id: store.user.oid });
    userStore.updateMetadata(metadata);
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "saveUserMetadata" });
    throw error;
  }
}

export async function getUserFromAzureAD(objectId: string, userId: string) {
  try {
    const response = await api.get("/getADUser", { oid: objectId, object_id: userId });
    return response.data || response;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getADUser", objectId, userId });
    throw error;
  }
}

export async function getADUserByUsername(username: string) {
  logInfo("Starting API call for username", "data-handler", {}, { username });
  try {
    logInfo("Making API request to /getADUserByUsername", "data-handler", {}, { username });
    const response = await api.get("/getADUserByUsername", { username });
    logInfo("API response received", "data-handler", {}, { username, hasData: !!response.data });
    return response.data || response;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "getADUserByUsername", username });
    throw error;
  }
}

export async function getTokenExpiration(): Promise<string> {
  try {
    const response = await api.get("/getTokenExpiration");
    return response.data.expiration;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getTokenExpiration" });
    throw error;
  }
}

/******************************************** */
/* VIEWS */
/******************************************** */

export async function getViewsByCustomer(customer_id: string): Promise<View[]> {
  const store = getUserStore();
  try {
    // this is a json hard coded list, not response.data
    const data = await api.get("/getViewsByCustomer", { customer_id, object_id: store.user.oid });
    return data;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getViewsByCustomer", customer_id });
    return [];
  }
}

/******************************************** */
/* RESULTS */
/******************************************** */

export async function getResultsByCustomer(customer_id: string): Promise<ResultsData> {
  const store = getUserStore();
  try {
    const data = await api.get("/getResultsByCustomer", { customer_id, object_id: store.user.oid });
    return data;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getResultsByCustomer", customer_id });
    return { results: [] };
  }
}

/******************************************** */
/* LOOKUPS */
/******************************************** */

export interface LookupItem {
  id: string;
  name: string;
}

export interface LookupMetadata {
  list: LookupItem[];
  type: "single-select" | "multi-select";
  level: "project" | "customer";
  can_override: boolean;
}

export interface GlobalLookup {
  id: string;
  global_lookup_id: string;
  version: number;
  state: string;
  metadata: Record<string, LookupMetadata>;
}

export interface LookupsData {
  success: boolean;
  data: GlobalLookup[];
}

export async function getAllLookups(): Promise<LookupsData> {
  try {
    const store = getUserStore();
    const response = await api.get("/getLookups", {
      object_id: store.user.oid,
    });
    return response;
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", {}, { operation: "getAllLookups" });
    return {
      success: false,
      data: [],
    };
  }
}

export async function createDraftLookup(): Promise<LookupsData> {
  try {
    const store = getUserStore();
    const response = await api.post("/createDraftLookup", {}, { object_id: store.user.oid });
    return response.data;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "createDraftLookup" });
    return { success: false, data: [] };
  }
}

export async function deleteLookup(lookupId: string): Promise<boolean> {
  try {
    const store = getUserStore();
    const response = await api.delete(`/lookups/${lookupId}`, { object_id: store.user.oid });
    return response.success;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "deleteLookup", lookupId });
    return false;
  }
}

export async function updateLookup(lookupId: string, section: string, items: LookupItem[]): Promise<boolean> {
  try {
    const store = getUserStore();
    const response = await api.put(`/lookups/${lookupId}`, { section, items }, { object_id: store.user.oid });
    return response.success;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "updateLookup", lookupId, section, itemsCount: items.length.toString() });
    return false;
  }
}

export async function activateLookup(lookupId: string): Promise<boolean> {
  try {
    const store = getUserStore();
    const response = await api.put(`/lookups/${lookupId}/activate`, {}, { object_id: store.user.oid });
    return response.success;
  } catch (error) {
    if (error instanceof TokenExpiredError) {
      throw error;
    }
    logError(error instanceof Error ? error : new Error("Unknown error"), "data-handler", undefined, { operation: "activateLookup", lookupId });
    return false;
  }
}
